import { useEffect, useRef, useState } from 'react';
import { useIsMounted } from './use-is-mounted';
import { shallowCompare } from './shallow-diff';
/**
 * A convenience hook to handle promises in React components, where the result
 * of the promise is needed.
 *
 * I've taken inspiration from the `useImport` hook to use the `useIsMounted` hook.
 *
 * @param makePromise `() => Promise<T>`
 * @param dependencies `DependencyList`
 * @param options.withTrailing `boolean`: keeps the previous resolved value when resolving new promise
 * @returns `{status: 'pending'} | {status: 'resolved'; result: T}`
 */
export const usePromise = (makePromise, dependencies, options = {}) => {
    const { withTrailing } = options;
    // Recreate the promise only when `dependencies` change, mirroring the `useEffect` deps
    // contract. We memoise manually via a ref (rather than `useMemo` with a caller-provided deps
    // array) because a dynamic dependency list cannot be statically verified by the hooks linter.
    const cacheRef = useRef(null);
    if (cacheRef.current === null || shallowCompare(cacheRef.current.dependencies, dependencies)) {
        cacheRef.current = { dependencies, promise: makePromise() };
    }
    const stablePromise = cacheRef.current.promise;
    const [state, setState] = useState(makePending);
    const isMounted = useIsMounted();
    useEffect(() => {
        stablePromise
            .then(result => {
            if (isMounted()) {
                setState(wrapResolved(result));
            }
        })
            .catch(exception => {
            if (isMounted()) {
                setState(wrapRejected(exception));
            }
        });
        return () => {
            if (!withTrailing) {
                setState(makePending());
            }
        };
    }, [isMounted, stablePromise, withTrailing]);
    return state;
};
const wrapResolved = (result) => ({
    status: 'resolved',
    result,
});
const wrapRejected = (exception) => ({
    status: 'rejected',
    exception,
});
export const makePending = () => ({ status: 'pending' });
export const wrapPromise = (promise) => {
    return promise
        .then(result => {
        return wrapResolved(result);
    })
        .catch(exception => {
        return wrapRejected(exception);
    });
};
